import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import IcomoonReact from 'icomoon-react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';

import Button from '../../../shared/_TBComponents/Button';
import Checkmark from '../../../shared/_TBComponents/Checkmark';
import { downloadSharedWorkout } from '../ducks/sharedWorkoutActions';
import iconSet from '../../../shared/images/teambuildr-selection.json';
import Text from '../../../shared/_TBComponents/Text';

const DownloadContainer = styled('div')`
  align-items: center;
  display: flex;
  flex-direction: column;
  padding: 30px;
`;

const DownloadWrapper = styled('div')`
  background: #ffffff;
  border-radius: 6px;
  height: auto;
  position: relative;
  width: 100%;

  .icon {
    cursor: pointer;
    position: absolute;
    right: 10px;
    top: 10px;
  }
`;

const ModalContainer = styled('div')`
  align-items: center;
  animation: animatebottom 0.2s;
  display: flex;
  flex-direction: column;
  justify-content: center;
  left: 50%;
  position: fixed;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 350px;
  z-index: 1001;

  @keyframes animatebottom {
    from {
      opacity: 0;
      transform: translate(-50%, 100%);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%);
    }
  }

  @media screen and (max-width: 768px) {
    width: 100%;
  }
`;

const SelectContainer = styled('div')`
  margin: 20px 0;
  select:focus {
    outline: none;
  }
`;

const StyledSelect = styled('select')`
  border-color: ${({ theme }) => theme.colors.green};
  cursor: pointer;
`;

const StyledOption = styled('option')`
  font-family: Nunito Sans;
  font-size: 14px;
`;

const ModalDownload = ({ closeModal }) => {
  const isDownloading = useSelector((state) => state.sharedWorkout.ui.isDownloading);
  const isDownloadComplete = useSelector((state) => state.sharedWorkout.ui.isDownloadComplete);
  const downloadPending = useSelector((state) => state.sharedWorkout.ui.downloadPending);
  const programData = useSelector((state) => state.sharedWorkout.data.programData);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const [selectedOption, setSelectedOption] = useState('importedExercises');
  const dispatch = useDispatch();
  const theme = useTheme();

  // Legacy parity: importing is coach-only, guarding the edge case of an athlete
  // reaching this modal (the transfer endpoint is coach-scoped and would 403 anyway).
  const isAthlete = currentUser.admin === 0;

  const handleStartDownload = () => {
    // currentUser can lag briefly on the sign-in path; don't fire without an account code.
    if (!currentUser.accountCode) return;
    dispatch(downloadSharedWorkout({
      targetAccountCode: currentUser.accountCode,
      savedWorkoutId: programData.savedWorkoutId,
      // Matches the legacy exer_db choice: 1 = Imported holding-pen, 0 = Master DB.
      targetExerciseDatabase: selectedOption === 'importedExercises' ? 1 : 0,
    }));
  };

  let content;
  if (isAthlete && !isDownloadComplete) {
    content = (
      <DownloadContainer>
        <Text styledTextProps={{ fontSize: '16px', fontWeight: '500' }}>
          Coach account required
        </Text>
        <Text
          styledTextProps={{ fontSize: '14px', color: '#9E9E9E' }}
          styledWrapperProps={{ margin: '10px 0 0' }}
        >
          Importing a program requires a coach account.
        </Text>
      </DownloadContainer>
    );
  } else if (isDownloadComplete) {
    content = (
      <DownloadContainer>
        <Checkmark styledWrapperProps={{ margin: '5px 0 15px' }} />
        <Text styledTextProps={{ fontSize: '18px', fontWeight: '500' }}>
          {downloadPending ? 'Download Started' : 'Download Complete'}
        </Text>
        <Text
          styledTextProps={{ fontSize: '14px', color: '#9E9E9E' }}
          styledWrapperProps={{ margin: '5px 0 20px' }}
        >
          {downloadPending
            ? 'Your program is importing and will appear in your account shortly.'
            : 'This program is now in your account.'}
        </Text>
        <Button
          bgColor={theme.colors.green}
          fontSize='14px'
          fullWidth
          name='Go to Dashboard'
          noBorder
          onClick={() => window.location.assign('/dashboard')}
          rounded
          txtColor={theme.colors.white}
          upperCase
        />
      </DownloadContainer>
    );
  } else {
    content = (
      <DownloadContainer>
        <Text styledTextProps={{ fontSize: '18px', fontWeight: '500' }}>
          Download Exercises To
        </Text>
        <SelectContainer>
          <StyledSelect
            disabled={isDownloading}
            onChange={(e) => setSelectedOption(e.target.value)}
            theme={theme}
            value={selectedOption}
          >
            <StyledOption value='importedExercises'>
              Imported Exercises (Recommended)
            </StyledOption>
            <StyledOption value='masterDatabase'>
              Master Database
            </StyledOption>
          </StyledSelect>
        </SelectContainer>
        <Button
          bgColor={theme.colors.green}
          disabled={isDownloading || !currentUser.accountCode}
          fontSize='14px'
          fullWidth
          isLoading={isDownloading}
          lightSpinner
          name='Start Download'
          noBorder
          onClick={handleStartDownload}
          rounded
          txtColor={theme.colors.white}
          upperCase
        />
      </DownloadContainer>
    );
  }

  return (
    <ModalContainer>
      <DownloadWrapper>
        <IcomoonReact className='icon' icon='remove' iconSet={iconSet} onClick={closeModal} size={15} />
        {content}
      </DownloadWrapper>
    </ModalContainer>
  );
};

ModalDownload.propTypes = {
  closeModal: PropTypes.func.isRequired,
};

export default ModalDownload;
